import * as React from "react";
import { Check, GalleryHorizontalEnd, LayoutGrid } from "lucide-react";
import { Skeleton } from "@/components/ui";
import { cn } from "@/lib/utils";
import { DesignPackageSummary } from "@/services/bedrock/dtos/DesignPackage";

export interface DesignChooserProps {
  designs: DesignPackageSummary[];
  loading: boolean;
  selectedId: number | null;
  onSelect: (id: number) => void;
  /** Marks looks that post several images; off where every look is a carousel. */
  showMultiImageHint?: boolean;
  /** With `onBrowse`, at most this many tiles show in one row; the rest wait behind the gallery tile. */
  maxVisible?: number;
  /** Opens the gallery of every look. */
  onBrowse?: () => void;
}

const GRID = "grid grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-3";
const BROWSE_TILE_WIDTH = "3rem";

/**
 * The looks shown in the row: the newest, with the selected one swapped in when it sits further
 * down, so the choice is always visible.
 */
export function visibleLooks(
  designs: DesignPackageSummary[],
  selectedId: number | null,
  maxVisible: number
): DesignPackageSummary[] {
  if (designs.length <= maxVisible) return designs;
  const first = designs.slice(0, maxVisible);
  const selected = designs.find(design => design.id === selectedId);
  if (!selected || first.includes(selected)) return first;
  return [...first.slice(0, maxVisible - 1), selected];
}

/**
 * The looks as same-size square tiles, each thumbnail fitted whole inside its square whatever its
 * shape, so rows line up. One radio group: arrow keys move the selection. With a gallery to open,
 * the row stays one line and a narrow tile at its end leads to the rest.
 */
export const DesignChooser: React.FC<DesignChooserProps> = ({
  designs,
  loading,
  selectedId,
  onSelect,
  showMultiImageHint = false,
  maxVisible,
  onBrowse,
}) => {
  const tiles = React.useRef<(HTMLButtonElement | null)[]>([]);

  if (loading) {
    return (
      <div className={GRID}>
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="aspect-square w-full rounded-xl" />
        ))}
      </div>
    );
  }
  if (designs.length === 0) {
    return <p className="text-sm text-slate-500">Your marketing team hasn't published any designs yet.</p>;
  }

  const rowed = !!onBrowse && maxVisible != null && designs.length > maxVisible;
  const shown = rowed ? visibleLooks(designs, selectedId, maxVisible) : designs;
  const hidden = designs.length - shown.length;

  const selectedIndex = shown.findIndex(design => design.id === selectedId);
  // Only one tile is in the tab order; the arrows move between them.
  const focusIndex = selectedIndex >= 0 ? selectedIndex : 0;

  const onKeyDown = (event: React.KeyboardEvent, index: number) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const next = (index + step + shown.length) % shown.length;
    onSelect(shown[next].id);
    tiles.current[next]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label="Looks"
      className={rowed ? "grid gap-3" : GRID}
      style={
        rowed ? { gridTemplateColumns: `repeat(${shown.length}, minmax(0, 1fr)) ${BROWSE_TILE_WIDTH}` } : undefined
      }
      data-testid="design-chooser"
    >
      {shown.map((design, index) => {
        const selected = design.id === selectedId;
        const multiImage = showMultiImageHint && design.slidesCount > 1;
        return (
          <button
            key={design.id}
            ref={el => {
              tiles.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={multiImage ? `${design.name}, several images` : design.name}
            title={design.name}
            tabIndex={index === focusIndex ? 0 : -1}
            onClick={() => onSelect(design.id)}
            onKeyDown={event => onKeyDown(event, index)}
            className={cn(
              "relative aspect-square w-full rounded-xl border bg-slate-100 p-1.5 transition cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
              selected
                ? "border-transparent ring-2 ring-primary ring-offset-2"
                : "border-slate-200 hover:border-slate-400"
            )}
          >
            {design.thumbnailUrl ? (
              <img src={design.thumbnailUrl} alt="" className="size-full rounded-md object-contain" />
            ) : (
              <span className="block size-full rounded-md bg-slate-200" />
            )}
            {selected && (
              <span className="absolute -top-1.5 -right-1.5 flex size-5 items-center justify-center rounded-full bg-primary text-white shadow">
                <Check className="size-3" strokeWidth={3} />
              </span>
            )}
            {multiImage && (
              <span
                className="absolute right-2 bottom-2 flex size-5 items-center justify-center rounded-md bg-slate-900/70 text-white"
                aria-hidden="true"
                data-testid="multi-image-hint"
              >
                <GalleryHorizontalEnd className="size-3" />
              </span>
            )}
          </button>
        );
      })}
      {rowed && (
        <button
          type="button"
          aria-label={`Browse all ${designs.length} looks`}
          title="Browse all looks"
          onClick={onBrowse}
          className="flex h-full w-full flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-slate-300 bg-white text-slate-500 transition cursor-pointer outline-none hover:border-slate-400 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-primary/50"
          data-testid="browse-looks"
        >
          <LayoutGrid className="size-4" aria-hidden="true" />
          <span className="text-[11px] font-semibold tabular-nums" aria-hidden="true">
            +{hidden}
          </span>
        </button>
      )}
    </div>
  );
};
